/**
 * Team Performance dashboard — admin-only "is the team executing?"
 *
 * Surfaces:
 *   - Per-rep table (demos, rooms opened, won, distributions sent,
 *     avg engagement % with trend delta, win rate)
 *   - Top trailers across the team by rewatch density (segment count)
 *     plus completion %. Team-library candidates.
 *   - Waiting on you: per-rep count of distributions whose last
 *     message is from a prospect — i.e., the seller hasn't replied.
 *     Clicking YOUR OWN row opens the Conversations inbox (the actual
 *     next step); another rep's row filters Deal Cycle to them (an
 *     admin can't read teammates' inboxes).
 *
 * Fetches GET /api/dashboard/team-performance?period=... — admin-
 * gated server-side. If a non-admin somehow gets here, the endpoint
 * returns 403 and we render an inline message.
 *
 * Styling: shared .dash-card system in App.css (2026-07-26 design
 * audit item 5); table cells keep their local layout styles.
 */

import { useEffect, useState } from 'react';
import DeltaChip from './DeltaChip';
import { initials, periodLabel } from '../utils/dashFormat';

const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';

export default function TeamPerformanceDashboard({ authToken, period, onSelectRep, onOpenDemo, onOpenInbox, currentUserId, authFetch }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Bumped by the Retry button; re-runs the fetch effect (Tier-1).
  const [retryNonce, setRetryNonce] = useState(0);
  // Prefer the app's authFetch (central 401 session-expiry handling).
  const doFetch = (url, opts) => (authFetch
    ? authFetch(url, opts)
    : fetch(url, { ...opts, headers: { ...(opts && opts.headers), Authorization: `Bearer ${authToken}` } }));

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (period) params.set('period', period);
        const r = await doFetch(`${API_BASE}/api/dashboard/team-performance?${params}`);
        if (r.status === 403) throw new Error('Team Performance is admin-only.');
        if (!r.ok) throw new Error(`Failed to load (${r.status})`);
        const json = await r.json();
        if (!cancelled) setData(json);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [authToken, period, retryNonce]);

  if (loading && !data) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: 'var(--room-text-muted)' }}>
        Loading team performance…
      </div>
    );
  }
  if (error) {
    return (
      <div style={{
        padding: 16, borderRadius: 12,
        background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.3)',
        color: '#dc2626', fontSize: 14, textAlign: 'center',
      }}>
        {error}
        <div style={{ marginTop: 10 }}>
          <button type='button' onClick={() => setRetryNonce((n) => n + 1)} style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.4)', background: '#fff', color: '#b91c1c', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Retry</button>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const { reps, topTrailers, waitingOnYou } = data;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Per-rep table */}
      <div className="dash-card">
        <div className="dash-card-head">
          <h3 className="dash-card-title">Per-rep performance</h3>
          <span className="dash-card-sub">{periodLabel(data.period)}</span>
        </div>
        {reps.length === 0 ? (
          <div className="dash-empty">No team members yet.</div>
        ) : (
          <div style={{ marginTop: 14, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--room-border)' }}>
                  <th style={thStyle}>Rep</th>
                  <th style={thStyleRight}>Demos</th>
                  <th style={thStyleRight}>Rooms</th>
                  <th style={thStyleRight}>Won</th>
                  <th style={thStyleRight}>Win rate</th>
                  <th style={thStyleRight}>Avg engagement</th>
                  <th style={thStyleRight} title="Rep's average share of speaking time on their own calls (multi-track calls only)">Talk ratio</th>
                </tr>
              </thead>
              <tbody>
                {reps.map((r, i) => {
                  const clickable = !!onSelectRep;
                  return (
                  <tr
                    key={r.userId}
                    onClick={() => clickable && onSelectRep(r.userId, r.userName)}
                    style={{
                      // Token-based striping (was hardcoded #fff/#f8fafc
                      // with a mismatched hover reset — dark-mode landmine).
                      background: i % 2 === 0 ? 'transparent' : 'rgba(var(--on-bg), 0.02)',
                      borderBottom: '1px solid rgba(var(--on-bg), 0.05)',
                      cursor: clickable ? 'pointer' : 'default',
                      transition: 'background 0.12s ease',
                    }}
                    onMouseEnter={(e) => { if (clickable) e.currentTarget.style.background = 'rgba(99, 102, 241, 0.07)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : 'rgba(var(--on-bg), 0.02)'; }}
                    title={clickable ? `Filter Deal Cycle by ${r.userName}` : undefined}
                  >
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 28, height: 28, flex: 'none', borderRadius: '50%',
                          background: 'rgba(99, 102, 241, 0.12)', color: '#6366f1',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 700,
                        }}>
                          {initials(r.userName)}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: 'var(--room-text-strong)' }}>{r.userName}</div>
                          {r.userEmail && (
                            <div style={{ fontSize: 11, color: 'var(--room-text-subtle)' }}>{r.userEmail}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={tdStyleRight}>{r.demosCount}</td>
                    <td style={tdStyleRight}>{r.roomsOpened}</td>
                    <td style={tdStyleRight}>{r.wonCount}</td>
                    <td style={tdStyleRight}>
                      <span style={r.winRate >= 20 ? winRateGood : (r.winRate > 0 ? winRateMid : winRateZero)}>
                        {r.winRate}%
                      </span>
                    </td>
                    <td style={tdStyleRight}>
                      <span style={{ color: r.avgCompletionPct >= 50 ? '#047857' : 'var(--room-text-body)' }}>
                        {r.avgCompletionPct}%
                      </span>
                      {/* Point delta vs the previous window (null on 'all'). */}
                      <DeltaChip curr={r.avgCompletionPct} prev={r.prevAvgCompletionPct} mode="pts" />
                    </td>
                    <td style={tdStyleRight}>
                      {/* Coaching heuristic: listeners win — under ~45%
                          talk share is healthy, above 60% flags a
                          monologue habit. Null = no multi-track calls
                          in the window. */}
                      {r.avgTalkRatioPct == null ? (
                        <span style={{ color: 'var(--room-text-subtle)' }}>—</span>
                      ) : (
                        <span style={{
                          fontWeight: 700,
                          color: r.avgTalkRatioPct > 60 ? '#dc2626'
                               : r.avgTalkRatioPct > 45 ? '#d97706'
                               : '#047857',
                        }}>
                          {r.avgTalkRatioPct}%
                        </span>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Top trailers + Waiting on you, side-by-side */}
      {/* auto-fit → single column on narrow screens (Tier-2 mobile fix) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
        <div className="dash-card">
          <div className="dash-card-head">
            <h3 className="dash-card-title">Top trailers</h3>
            <span className="dash-card-sub">by rewatch density</span>
          </div>
          {topTrailers.length === 0 ? (
            <div className="dash-empty">
              No engagement segments yet. Top trailers appear once prospects start watching.
            </div>
          ) : (
            <ul className="dash-list">
              {topTrailers.map((t) => {
                const clickable = !!onOpenDemo;
                return (
                <li key={t.demoId}>
                  <button
                    type="button"
                    onClick={() => clickable && onOpenDemo(t.demoId)}
                    disabled={!clickable}
                    className="dash-list-btn"
                    title={clickable ? 'Open this trailer' : undefined}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--room-text-strong)' }}>
                        {t.prospectName || t.filename || 'Untitled trailer'}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--room-text-subtle)', marginTop: 2 }}>
                        {t.ownerName} · {t.distributionCount} share{t.distributionCount === 1 ? '' : 's'} ·{' '}
                        {t.avgCompletionPct}% avg completion
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#7c3aed' }}>
                        {t.segmentCount}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--room-text-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        plays
                      </div>
                    </div>
                  </button>
                </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="dash-card">
          <div className="dash-card-head">
            <h3 className="dash-card-title">Waiting on you</h3>
            <span className="dash-card-sub">unresponded threads</span>
          </div>
          {waitingOnYou.length === 0 ? (
            <div className="dash-empty">
              Inbox zero. Every prospect message has a seller reply.
            </div>
          ) : (
            <ul className="dash-list">
              {waitingOnYou.map((w) => {
                // Your own row → the inbox (that's where the reply
                // happens). Another rep's row → Deal Cycle filtered to
                // them (their inbox isn't visible to you).
                const isSelf = currentUserId != null && Number(w.userId) === Number(currentUserId);
                const action = isSelf && onOpenInbox
                  ? () => onOpenInbox()
                  : (onSelectRep ? () => onSelectRep(w.userId, w.userName) : null);
                return (
                <li key={w.userId}>
                  <button
                    type="button"
                    onClick={action || undefined}
                    disabled={!action}
                    className="dash-list-btn dash-list-btn--warn"
                    title={action ? (isSelf ? 'Open your Conversations inbox' : `Filter Deal Cycle by ${w.userName}`) : undefined}
                  >
                    <div style={{
                      width: 28, height: 28, flex: 'none', borderRadius: '50%',
                      background: 'rgba(245, 158, 11, 0.14)', color: '#b45309',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 700,
                    }}>
                      {initials(w.userName)}
                    </div>
                    <div style={{ flex: 1, fontSize: 14, fontWeight: 600, color: 'var(--room-text-strong)' }}>
                      {w.userName}
                      {isSelf && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: '#b45309' }}>(you)</span>}
                    </div>
                    <span style={{
                      padding: '3px 10px', borderRadius: 100,
                      background: 'rgba(245, 158, 11, 0.14)', color: '#b45309',
                      fontSize: 12, fontWeight: 700,
                    }}>
                      {w.waitingCount} thread{w.waitingCount === 1 ? '' : 's'}
                    </span>
                  </button>
                </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── table-local styles (kept: table-specific, not card chrome) ──

const thStyle = {
  padding: '10px 12px', textAlign: 'left',
  fontSize: 11, fontWeight: 700, color: 'var(--room-text-muted)',
  textTransform: 'uppercase', letterSpacing: '0.06em',
};
const thStyleRight = { ...thStyle, textAlign: 'right' };
const tdStyle = { padding: '12px', verticalAlign: 'middle' };
const tdStyleRight = { ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'var(--room-text-body)' };
const winRateGood = { color: '#047857', fontWeight: 700 };
const winRateMid  = { color: '#d97706', fontWeight: 700 };
const winRateZero = { color: 'var(--room-text-subtle)', fontWeight: 600 };
